import { defineStore } from "pinia";
import { ref, computed } from "vue";
import DarkMode from "@services/theme/DarkMode.js";

// Helpers
function applyTheme(preference) {
  const w = typeof window !== "undefined" ? window : null;
  const darkFromSystem = !!(
    w &&
    w.matchMedia &&
    w.matchMedia("(prefers-color-scheme: dark)").matches
  );
  const isDarkMode =
    preference === "dark" || (preference === "system" && darkFromSystem);

  // Single source of truth: Tailwind's .dark class on <html>
  DarkMode.setDarkMode(!!isDarkMode);
}

function savePrefsToLocalStorage(themePreference) {
  if (typeof localStorage === "undefined") return;
  const prefs = {
    themePreference,
  };
  localStorage.setItem("Preferences", JSON.stringify(prefs));
}

// Initialize system preference listener when store is loaded
let systemPreferenceListener = null;

function initializeSystemPreferenceListener() {
  if (typeof window === "undefined") return;
  if (window.matchMedia && !systemPreferenceListener) {
    const darkModeMediaQuery = window.matchMedia(
      "(prefers-color-scheme: dark)"
    );

    systemPreferenceListener = (e) => {
      // Get current preference from localStorage since we don't have store access here
      try {
        const prefs = JSON.parse(localStorage.getItem("Preferences")) || {};
        // Only apply changes if user has selected system preference
        if (prefs.themePreference === "system") {
          applyTheme("system");
        }
      } catch (error) {
        console.error("Error processing theme preference:", error);
      }
    };

    // Use the newer addEventListener if supported, otherwise use the older addListener
    if (darkModeMediaQuery.addEventListener) {
      darkModeMediaQuery.addEventListener("change", systemPreferenceListener);
    } else {
      darkModeMediaQuery.addListener(systemPreferenceListener);
    }
  }
}

// Initialize global function for verifying dark theme
if (typeof window !== "undefined" && typeof document !== "undefined") {
  window.isDarkTheme = function () {
    return document.documentElement.classList.contains("dark");
  };
}

export const usePreferencesStore = defineStore("preferences", () => {
  // State
  const version = ref("190");
  const themePreference = ref("system"); // Options: 'system', 'light', 'dark'

  // Getters
  const darkMode = computed(() => {
    if (themePreference.value === "system") {
      const w = typeof window !== "undefined" ? window : null;
      return !!(
        w &&
        w.matchMedia &&
        w.matchMedia("(prefers-color-scheme: dark)").matches
      );
    }
    return themePreference.value === "dark";
  });

  // Actions
  function setDurablePrefs({ themePreference: newThemePreference }) {
    themePreference.value = newThemePreference || "system";
    applyTheme(themePreference.value);
    savePrefsToLocalStorage(themePreference.value);
  }

  function setThemePreference(preference) {
    themePreference.value = preference;
    applyTheme(themePreference.value);
    savePrefsToLocalStorage(themePreference.value);
  }

  function loadPreferences() {
    try {
      const prefs =
        typeof localStorage !== "undefined"
          ? JSON.parse(localStorage.getItem("Preferences")) || {}
          : {};

      // Use setter to apply side effects
      setDurablePrefs({
        themePreference: prefs.themePreference,
      });
    } catch {}

    // Initialize system preference listener
    initializeSystemPreferenceListener();
  }

  return {
    version,
    themePreference,
    darkMode,
    setDurablePrefs,
    setThemePreference,
    loadPreferences,
  };
});
